import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonBackButton,
  IonButtons,
  IonToast,
  IonButton,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonCard,
  IonCardContent,
  IonInput,
  IonItem,
  IonList,
  IonRange,
  IonText,
  IonIcon,
  IonLoading,
  IonSelect,
  IonSelectOption
} from '@ionic/react';
import SpinWheelComponent from '../../components/SpinWheel/SpinWheel';
import { Prize, SpinResult } from '../../models/SpinWheel';
import { SpinWheelService, SpinWheelConfig } from '../../data-services/spinWheel.service';
import { add, remove, refresh, swapHorizontal, chevronDown } from 'ionicons/icons';
import './SpinWheelPage.css';

const SpinWheelPage: React.FC = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [viewMode, setViewMode] = useState<'wheel' | 'config'>('wheel');
  
  // Loading state for API calls
  const [loading, setLoading] = useState(true);
  
  // Available wheel configurations from API
  const [wheelConfigs, setWheelConfigs] = useState<SpinWheelConfig[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');
  const [activeConfig, setActiveConfig] = useState<SpinWheelConfig | null>(null);
  
  // Wheel display properties
  const [wheelSize, setWheelSize] = useState(300);
  const [spinDuration, setSpinDuration] = useState(5);
  const [centerImage, setCenterImage] = useState('/assets/images/spinwheel/logo.svg');
  
  // Prize configuration
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [customPrizes, setCustomPrizes] = useState<{
    name: string;
    probability: number;
    image: string;
  }[]>([]);

  // Load wheel configurations from API on component mount
  useEffect(() => {
    const loadWheelConfigurations = async () => {
      try {
        setLoading(true);
        const configs = await SpinWheelService.getWheelConfigurations();
        setWheelConfigs(configs);
        
        // Load the active configuration by default
        const active = await SpinWheelService.getActiveWheelConfiguration();
        if (active) {
          setActiveConfig(active);
          setSelectedConfigId(active.id);
          setPrizes(active.prizes);
          setWheelSize(active.defaultSize);
          setSpinDuration(active.defaultSpinDuration);
          setCenterImage(active.centerImage);
          
          // Initialize custom prizes for editing
          setCustomPrizes(active.prizes.map(prize => ({
            name: prize.name,
            probability: prize.probability,
            image: prize.image
          })));
        }
      } catch (error) {
        console.error('Error loading wheel configurations:', error);
        setToastMessage('Failed to load wheel configurations');
        setShowToast(true);
      } finally {
        setLoading(false);
      }
    };
    
    loadWheelConfigurations();
  }, []);

  // Load selected configuration when selection changes
  useEffect(() => {
    const loadSelectedConfig = async () => {
      if (!selectedConfigId) return;
      
      try {
        setLoading(true);
        const config = await SpinWheelService.getWheelConfigurationById(selectedConfigId);
        if (config) {
          setActiveConfig(config);
          setPrizes(config.prizes);
          setWheelSize(config.defaultSize);
          setSpinDuration(config.defaultSpinDuration);
          setCenterImage(config.centerImage);
          
          // Initialize custom prizes for editing
          setCustomPrizes(config.prizes.map(prize => ({
            name: prize.name,
            probability: prize.probability,
            image: prize.image
          })));
        }
      } catch (error) {
        console.error('Error loading wheel configuration:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadSelectedConfig();
  }, [selectedConfigId]);

  // Update prizes when custom prizes change in config mode
  useEffect(() => {
    if (viewMode === 'config' && customPrizes.length > 0) {
      const newPrizes = customPrizes.map((p, index) => ({
        id: `${index + 1}`,
        name: p.name,
        probability: p.probability,
        image: p.image,
        backgroundColor: `hsl(${(index * 360 / customPrizes.length) % 360}, 70%, 60%)`
      }));
      setPrizes(newPrizes);
    }
  }, [customPrizes, viewMode]);

  // Handle spin completion
  const handleSpinComplete = (result: SpinResult) => {
    if (activeConfig) {
      // Record spin result to API
      SpinWheelService.recordSpinResult(activeConfig.id, result.prize.id);
      
      // Show success message
      setToastMessage(`Congratulations! You won: ${result.prize.name}`);
      setShowToast(true);
    }
  };

  // Add a new prize
  const addPrize = () => {
    const newIndex = customPrizes.length;
    const newPrizeName = `Prize ${newIndex + 1}`;
    setCustomPrizes([...customPrizes, {
      name: newPrizeName,
      probability: 1 / (customPrizes.length + 1),
      image: `/assets/images/spinwheel/hat.svg` // Default to hat image for new prizes
    }]);
    
    // Normalize probabilities
    normalizeProbabilities();
  };

  // Remove a prize
  const removePrize = (index: number) => {
    if (customPrizes.length <= 2) return; // Minimum 2 prizes
    
    const newPrizes = [...customPrizes];
    newPrizes.splice(index, 1);
    setCustomPrizes(newPrizes);
    
    // Normalize probabilities
    normalizeProbabilities();
  };

  // Update a prize
  const updatePrize = (index: number, field: 'name' | 'probability' | 'image', value: string | number) => {
    const newPrizes = [...customPrizes];
    newPrizes[index] = {
      ...newPrizes[index],
      [field]: value
    };
    setCustomPrizes(newPrizes);
  };

  // Normalize probabilities to ensure they sum to 1
  const normalizeProbabilities = () => {
    const totalProbability = customPrizes.reduce((sum, prize) => sum + prize.probability, 0);
    
    if (totalProbability === 0) {
      // If all probabilities are 0, assign equal probabilities
      const equalProbability = 1 / customPrizes.length;
      setCustomPrizes(customPrizes.map(p => ({...p, probability: equalProbability})));
    } else if (Math.abs(totalProbability - 1) > 0.01) {
      // If probabilities don't sum to 1 (with a small tolerance), normalize them
      setCustomPrizes(customPrizes.map(p => ({
        ...p,
        probability: p.probability / totalProbability
      })));
    }
  };

  // Reset to equal probabilities
  const resetProbabilities = () => {
    const equalProbability = 1 / customPrizes.length;
    setCustomPrizes(customPrizes.map(p => ({...p, probability: equalProbability})));
  };

  // Apply configuration and switch to wheel view
  const applyConfiguration = () => {
    normalizeProbabilities();
    setViewMode('wheel');
  };

  // Handle wheel configuration selection change
  const handleConfigChange = (configId: string) => {
    setSelectedConfigId(configId);
  };

  return (
    <IonPage>
      {/* <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" />
          </IonButtons>
          <IonTitle>Spin & Win</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => setViewMode(viewMode === 'wheel' ? 'config' : 'wheel')}>
              {viewMode === 'wheel' ? 'Configure' : 'View Wheel'}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader> */}
      
      <IonContent fullscreen className="spin-wheel-page">
        {/* Loading indicator */}
        <IonLoading isOpen={loading} message="Loading..." />
        
        {viewMode === 'wheel' ? (
          <div className="page-container">
            {/* Wheel configuration selector */}
            {wheelConfigs.length > 0 && (
              <>
                {/* <div className="wheel-selector">
                  <IonItem>
                    <IonLabel>Select Prize Wheel</IonLabel>
                    <IonSelect
                      value={selectedConfigId}
                      onIonChange={e => handleConfigChange(e.detail.value)}
                      interface="popover"
                      interfaceOptions={{ cssClass: 'wheel-select-popover' }}
                    >
                      {wheelConfigs.map(config => (
                        <IonSelectOption key={config.id} value={config.id}>
                          {config.name}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>
                </div> */}
              </>
            )}
            
            {activeConfig && (
              <>
                <h2 className="page-title">{activeConfig.name}</h2>
                <p className="page-subtitle">{activeConfig.description}</p>
                
                <SpinWheelComponent 
                  prizes={prizes}
                  onSpinComplete={handleSpinComplete}
                  size={wheelSize}
                  spinDuration={spinDuration}
                  centerImage={centerImage}
                />
                
                {/* <div className="prizes-legend">
                  <h3>Prizes</h3>
                  <div className="prizes-grid">
                    {prizes.map(prize => (
                      <div key={prize.id} className="prize-item">
                        <img src={prize.image} alt={prize.name} />
                        <p>{prize.name}</p>
                        <small>{(prize.probability * 100).toFixed(1)}%</small>
                      </div>
                    ))}
                  </div>
                </div> */}
              </>
            )}
            
            {!activeConfig && !loading && (
              <div className="no-config-message">
                <h3>No active wheel configuration found</h3>
                <p>Please configure a new wheel or select one from the available configurations</p>
              </div>
            )}
          </div>
        ) : (
          <div className="config-container">
            {/* Configuration selector */}
            {wheelConfigs.length > 0 && (
              <IonCard>
                <IonCardContent>
                  <h2>Select Wheel Template</h2>
                  <IonItem>
                    <IonLabel position="stacked">Wheel Configuration</IonLabel>
                    <IonSelect
                      value={selectedConfigId}
                      onIonChange={e => handleConfigChange(e.detail.value)}
                      interface="popover"
                    >
                      {wheelConfigs.map(config => (
                        <IonSelectOption key={config.id} value={config.id}>
                          {config.name}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>
                </IonCardContent>
              </IonCard>
            )}
            
            <IonCard>
              <IonCardContent>
                <h2>Wheel Settings</h2>
                
                <IonList>
                  <IonItem>
                    <IonLabel position="stacked">Wheel Size (px)</IonLabel>
                    <IonRange
                      min={200}
                      max={500}
                      step={10}
                      value={wheelSize}
                      onIonChange={e => setWheelSize(e.detail.value as number)}
                    />
                    <IonText slot="end">{wheelSize}px</IonText>
                  </IonItem>
                  
                  <IonItem>
                    <IonLabel position="stacked">Spin Duration (seconds)</IonLabel>
                    <IonRange
                      min={2}
                      max={10}
                      step={0.5}
                      value={spinDuration}
                      onIonChange={e => setSpinDuration(e.detail.value as number)}
                    />
                    <IonText slot="end">{spinDuration}s</IonText>
                  </IonItem>
                  
                  <IonItem>
                    <IonLabel position="stacked">Center Image</IonLabel>
                    <IonInput
                      value={centerImage}
                      onIonChange={e => setCenterImage(e.detail.value || '')}
                    />
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>
            
            <IonCard>
              <IonCardContent>
                <div className="prizes-header">
                  <h2>Prize Configuration</h2>
                  <IonButton size="small" fill="clear" onClick={resetProbabilities}>
                    <IonIcon slot="icon-only" icon={refresh} />
                  </IonButton>
                  <IonButton size="small" fill="clear" onClick={addPrize}>
                    <IonIcon slot="icon-only" icon={add} />
                  </IonButton>
                </div>
                
                <IonList>
                  {customPrizes.map((prize, index) => (
                    <IonItem key={index} className="prize-config-item">
                      <div className="prize-config-content">
                        <IonItem lines="none">
                          <IonLabel position="stacked">Name</IonLabel>
                          <IonInput
                            value={prize.name}
                            onIonChange={e => updatePrize(index, 'name', e.detail.value || '')}
                          />
                        </IonItem>
                        
                        <div className="probability-input">
                          <IonLabel position="stacked">Probability (%)</IonLabel>
                          <IonRange
                            min={1}
                            max={100}
                            step={1}
                            value={prize.probability * 100}
                            onIonChange={e => updatePrize(index, 'probability', (e.detail.value as number) / 100)}
                          />
                          <IonText>{(prize.probability * 100).toFixed(1)}%</IonText>
                        </div>
                        
                        <IonItem lines="none">
                          <IonLabel position="stacked">Image Path</IonLabel>
                          <IonInput
                            value={prize.image}
                            onIonChange={e => updatePrize(index, 'image', e.detail.value || '')}
                          />
                        </IonItem>
                        
                        {/* Image preview */}
                        <div className="image-preview">
                          <img src={prize.image} alt={prize.name} />
                        </div>
                      </div>
                      
                      <IonButton
                        fill="clear"
                        color="danger"
                        slot="end"
                        onClick={() => removePrize(index)}
                        disabled={customPrizes.length <= 2}
                      >
                        <IonIcon slot="icon-only" icon={remove} />
                      </IonButton>
                    </IonItem>
                  ))}
                </IonList>
                
                <IonButton expand="block" onClick={applyConfiguration} className="apply-button">
                  Apply Configuration
                </IonButton>
              </IonCardContent>
            </IonCard>
          </div>
        )}
        
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="middle"
          color="success"
        />
      </IonContent>
    </IonPage>
  );
};

export default SpinWheelPage;
